import type { LucideIcon } from "lucide-react"
import {
  Accessibility,
  BusFront,
  CalendarDays,
  CircleCheck,
  Clock,
  Coffee,
  Globe,
  Handshake,
  Heart,
  KeyRound,
  Lock,
  MapPin,
  MessageCircle,
  Sofa,
  SquareParking,
  Target,
  TrainFront,
  TrendingUp,
  VolumeX,
  Wifi,
} from "lucide-react"

// The navy line icons a tile can draw, and the names the admin's icon picker
// stores. Shared with FeatureListEditor so the editor offers exactly what the
// pages can render — the two lists cannot drift. One vocabulary serves both
// brands: the venue rows on "What's included" and the commitments on Gisela's
// About page, so a list added later inherits icons that already look right
// beside the others.
export const INCLUDED_ICONS: { name: string; label: string; icon: LucideIcon }[] = [
  { name: "parking", label: "Parking", icon: SquareParking },
  { name: "train", label: "Train", icon: TrainFront },
  { name: "bus", label: "Bus or tram", icon: BusFront },
  { name: "reception", label: "Reception or waiting room", icon: Sofa },
  { name: "kitchen", label: "Kitchen, tea & coffee", icon: Coffee },
  { name: "wifi", label: "Wi-Fi", icon: Wifi },
  { name: "keyless", label: "Keyless entry", icon: KeyRound },
  { name: "step-free", label: "Step-free access", icon: Accessibility },
  { name: "booking", label: "Online booking", icon: CalendarDays },
  { name: "quiet", label: "Sound masking", icon: VolumeX },
  { name: "hours", label: "Opening hours", icon: Clock },
  { name: "location", label: "Location", icon: MapPin },
  { name: "check", label: "Plain tick", icon: CircleCheck },
  { name: "target", label: "Evidence, aims", icon: Target },
  { name: "progress", label: "Progress, measurement", icon: TrendingUp },
  { name: "speaking", label: "Plain speaking", icon: MessageCircle },
  { name: "handshake", label: "Honesty, working together", icon: Handshake },
  { name: "languages", label: "Languages", icon: Globe },
  { name: "privacy", label: "Privacy, confidentiality", icon: Lock },
  { name: "care", label: "Care", icon: Heart },
]

const BY_NAME = new Map(INCLUDED_ICONS.map(({ name, icon }) => [ name, icon ]))

// Lists seeded before the picker existed stored an emoji. Those rows are live
// in every database and `upsert` never rewrites an existing section, so the
// emoji is translated to the line icon it stood for rather than migrated —
// which also keeps a hand-typed emoji from breaking a row of line icons.
const EMOJI_ICONS: Record<string, LucideIcon> = {
  "🎯": Target,
  "📈": TrendingUp,
  "🗣️": MessageCircle,
  "🗣": MessageCircle,
  "🤝": Handshake,
  "🌍": Globe,
  "🌎": Globe,
  "🌐": Globe,
  "🔒": Lock,
  "🔐": Lock,
  "❤️": Heart,
  "💙": Heart,
  "✅": CircleCheck,
  "☑️": CircleCheck,
  "🅿️": SquareParking,
  "🚗": SquareParking,
  "🚆": TrainFront,
  "🚂": TrainFront,
  "🚌": BusFront,
  "🛋️": Sofa,
  "☕": Coffee,
  "📶": Wifi,
  "🔑": KeyRound,
  "♿": Accessibility,
  "📅": CalendarDays,
  "🔇": VolumeX,
  "🕐": Clock,
  "📍": MapPin,
}

// When the icon slot is blank the icon comes from the label, so a row stays
// in one visual voice without anyone having to fill the field.
const ICON_RULES: [RegExp, LucideIcon][] = [
  [/park/i, SquareParking],
  [/train|rail/i, TrainFront],
  [/bus|tram|metrolink/i, BusFront],
  [/reception|waiting|welcom/i, Sofa],
  [/kitchen|tea|coffee|refresh/i, Coffee],
  [/wi-?fi|internet|broadband/i, Wifi],
  [/keyless|keypad|key ?code|door code/i, KeyRound],
  [/step-free|accessib|ground floor|level/i, Accessibility],
  [/booking|book|schedul/i, CalendarDays],
  [/sound|quiet|confidential|acoustic/i, VolumeX],
  [/hour|day|24|flexib|open/i, Clock],
  [/location|central|town|street|centre/i, MapPin],
  // Commitments, for lists like Gisela's "What I stand for". Narrower rules
  // come first: "Careful with your privacy" is a lock, not a heart, and "Your
  // own language" is the globe rather than the plain-speaking bubble.
  [/evidence|research|aim|goal|target/i, Target],
  [/progress|measure|outcome|score|result/i, TrendingUp],
  [/privacy|private|data|secure/i, Lock],
  [/spanish|bilingual|español|espanol|own language|both languages/i, Globe],
  [/plain|jargon|language|speak|clear/i, MessageCircle],
  [/honest|fit|together|collaborat/i, Handshake],
  [/care|kind|compassion|warm/i, Heart],
]

/** The icon a title alone earns, when the item names none. */
export function iconFor(title: string): LucideIcon {
  return ICON_RULES.find(([ rule ]) => rule.test(title))?.[1] ?? CircleCheck
}

/** The icon an item names, or undefined. A stored emoji resolves through the
 *  map above, so only an emoji with no line equivalent lands as itself. */
export function namedIcon(name: string): LucideIcon | undefined {
  return BY_NAME.get(name) ?? EMOJI_ICONS[name]
}

/** How the picker names a stored value it has no option for — an emoji that
 *  now draws a line icon is labelled by the icon, not by the emoji, so the
 *  option matches what the page shows. */
export function storedIconLabel(icon: string): string {
  const mapped = EMOJI_ICONS[icon]
  const label = mapped && INCLUDED_ICONS.find(({ icon: i }) => i === mapped)?.label
  return label ? `${label} (${icon})` : `Emoji ${icon}`
}

/** What a tile draws: the named icon, else the emoji it carries, else the
 *  icon its title earns. */
export function resolveIcon(icon: string, title: string): { Icon?: LucideIcon; emoji?: string } {
  if (icon === "") return { Icon: iconFor(title) }
  const named = namedIcon(icon)
  return named ? { Icon: named } : { emoji: icon }
}
